import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import { Progress } from '../ui/progress';
import { UserCircle, ChevronLeft, ChevronRight, Check, Save, Loader2, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import { useApi } from '../../hooks/useApi';

export default function PersonalBrandArchitect({ onProgress }) {
  const api = useApi();
  const [sections, setSections] = useState([]);
  const [answers, setAnswers] = useState({});
  const [progress, setProgress] = useState(null);
  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [iv, pb] = await Promise.all([
          api.get('/media-studio/interview'),
          api.get('/media-studio/personal-brand'),
        ]);
        setSections(iv.sections || []);
        setAnswers(pb.answers || {});
        setProgress(pb.progress || null);
        onProgress?.(pb.progress || null);
      } catch (e) {
        toast.error('Failed to load Personal Brand Architect');
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setField = (id, val) => setAnswers((p) => ({ ...p, [id]: val }));

  const saveSection = async (advance = true) => {
    const current = sections[step];
    if (!current) return;
    const patch = {};
    current.questions.forEach((q) => { patch[q.id] = answers[q.id] || ''; });
    setSaving(true);
    try {
      const res = await api.put('/media-studio/personal-brand', { answers: patch });
      setProgress(res.progress);
      onProgress?.(res.progress);
      toast.success('Progress saved');
      if (advance && step < sections.length - 1) setStep(step + 1);
    } catch (e) {
      toast.error(e.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-[#B89B5E]" /></div>;
  }

  const current = sections[step];
  const sectionMeta = progress?.sections?.find((s) => s.id === current?.id);

  return (
    <Card className="bg-white/80 backdrop-blur border-[#E8E6E1]" data-testid="personal-brand-architect">
      <CardHeader>
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-[#B89B5E]/10"><UserCircle className="h-5 w-5 text-[#B89B5E]" /></div>
            <div>
              <CardTitle className="text-lg">Personal Brand Architect</CardTitle>
              <p className="text-xs text-muted-foreground">A guided interview — your answers save as you go.</p>
            </div>
          </div>
          {progress?.ready_to_generate && (
            <Badge className="bg-emerald-600 text-white" data-testid="pb-ready-badge">
              <Check className="h-3 w-3 mr-1" /> Ready
            </Badge>
          )}
        </div>
        {progress && (
          <div className="pt-3">
            <div className="flex items-center justify-between text-xs text-muted-foreground mb-1">
              <span>{progress.answered} of {progress.total} answered</span>
              <span>{progress.percent}%</span>
            </div>
            <Progress value={progress.percent} className="h-2" data-testid="pb-progress" />
          </div>
        )}
      </CardHeader>
      <CardContent className="space-y-5">
        {/* Stepper */}
        <div className="flex flex-wrap gap-2">
          {sections.map((s, i) => {
            const meta = progress?.sections?.find((m) => m.id === s.id);
            const active = i === step;
            return (
              <button
                key={s.id}
                onClick={() => setStep(i)}
                data-testid={`pb-step-${s.id}`}
                className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${active ? 'bg-[#1A1A1A] text-white border-[#1A1A1A]' : meta?.complete ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-white text-stone-600 border-[#E8E6E1] hover:border-[#B89B5E]'}`}
              >
                {meta?.complete && <Check className="h-3 w-3 mr-1 inline" />}
                {s.title}
              </button>
            );
          })}
        </div>

        {current && (
          <div className="space-y-4" data-testid={`pb-section-${current.id}`}>
            <div>
              <h3 className="font-semibold text-[#1A1A1A]">{current.title}</h3>
              <p className="text-sm text-muted-foreground">{current.description}</p>
            </div>
            {current.questions.map((q) => (
              <div key={q.id}>
                <Label className="text-xs">{q.label} {q.required && <span className="text-red-500">*</span>}</Label>
                {q.type === 'text' ? (
                  <Input
                    value={answers[q.id] || ''}
                    onChange={(e) => setField(q.id, e.target.value)}
                    placeholder={q.placeholder}
                    data-testid={`pb-input-${q.id}`}
                  />
                ) : (
                  <Textarea
                    value={answers[q.id] || ''}
                    onChange={(e) => setField(q.id, e.target.value)}
                    placeholder={q.placeholder}
                    rows={3}
                    data-testid={`pb-input-${q.id}`}
                  />
                )}
              </div>
            ))}
          </div>
        )}

        <div className="flex items-center justify-between pt-2">
          <Button variant="outline" size="sm" disabled={step === 0} onClick={() => setStep(step - 1)} data-testid="pb-prev-btn">
            <ChevronLeft className="h-4 w-4 mr-1" /> Back
          </Button>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => saveSection(false)} disabled={saving} data-testid="pb-save-btn">
              {saving ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Save className="h-4 w-4 mr-1" />} Save
            </Button>
            <Button size="sm" className="bg-[#1A1A1A] hover:bg-[#2A2A2A] text-white" onClick={() => saveSection(true)} disabled={saving} data-testid="pb-next-btn">
              {step < sections.length - 1 ? (<>Save &amp; Next <ChevronRight className="h-4 w-4 ml-1" /></>) : (<>Finish <Sparkles className="h-4 w-4 ml-1" /></>)}
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
